import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { ShoppingCartIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Layout from '@/components/Layout';
import { useI18n } from '@/hooks/useI18n';
import { formatCurrency } from '@/utils/formatters';
import { apiFetch } from '@/utils/api';
import { isFeatureEnabled } from '@/utils/featureFlags';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import PaginationFooter from '@/components/PaginationFooter';
import { TagFilter } from '@/components/TagFilter';
import type { Tag } from '@/components/TagBadge';

interface CostCenter {
  id: string;
  name: string;
}

interface Pagination {
  page: number;
  total: number;
  pages: number;
  limit: number;
}

const OrdersPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
  const [orders, setOrders] = useState<any[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    costCenterId: '',
    dataSource: ''
  });
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    total: 0,
    pages: 0,
    limit: 20,
  });
  const [showAdminSyncButtons, setShowAdminSyncButtons] = useState<boolean>(true);
  const [tagFilteredIds, setTagFilteredIds] = useState<string[] | null>(null);


  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.costCenterId) params.append('cost_center_id', filters.costCenterId);
      if (filters.dataSource) params.append('data_source', filters.dataSource);
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());

      const response = await apiFetch(`/api/sales/orders?${params}`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
        if (data.pagination) {
          setPagination(prev => ({ ...prev, ...data.pagination }));
        }
      } else {
        throw new Error('Failed to fetch orders');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostCenters = async () => {
    try {
      const response = await apiFetch('/api/cost-centers/');
      if (response.ok) {
        const data = await response.json();
        setCostCenters(data);
      }
    } catch (err) {
      console.error('Failed to fetch cost centers:', err);
    }
  };



  useEffect(() => {
    fetchCostCenters();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [filters, pagination.page, pagination.limit]);

  // Load feature flags
    useEffect(() => {
        const loadFeatureFlags = async () => {
            try {
                const enabled = await isFeatureEnabled('enable_admin_sync_buttons');
                setShowAdminSyncButtons(enabled);
            } catch (error) {
                console.error('Error loading feature flags:', error);
                // Default to true on error
                setShowAdminSyncButtons(true);
            }
        };

        loadFeatureFlags();
    }, []);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleIncrementalSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    setError(null);

    try {
      const response = await apiFetch('/api/fruit-bi-credentials/sync-incremental-and-process', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start sync');
      }

      const result = await response.json();

      if (result.success) {
        if (result.status === 'no_work') {
          setSyncMessage(result.message);
        } else {
          setSyncMessage(`${result.message} You can check the progress in the sync status.`);
          // Refresh orders after a short delay to show newly synced data
          setTimeout(() => {
            fetchOrders();
          }, 2000);
        }
      } else {
        setError(result.message || 'Sync failed');
      }
    } catch (error) {
      console.error('Error during incremental sync:', error);
      if (error instanceof Error) {
        setError(`Failed to start incremental sync: ${error.message}`);
      } else {
        setError('Failed to start incremental sync');
      }
    } finally {
      setSyncing(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDataSourceBadge = (dataSource: string) => {
    const getVariant = (source: string): "default" | "secondary" | "destructive" | "outline" => {
      switch (source) {
        case 'fruitBI':
          return 'default';
        case 'square':
          return 'secondary';
        case 'toast':
          return 'outline';
        case 'manual':
        default:
          return 'secondary';
      }
    };

    return (
      <Badge variant={getVariant(dataSource)}>
        {dataSource}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t('salesOrders.errorLoading', 'finance')}</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
                  <h1 className="text-3xl font-bold tracking-tight">
              {t('navigation.orders', 'common')}
                  </h1>
                    <p className="text-muted-foreground">
                            {t('salesOrders.subtitle', 'finance')}
                    </p>
            </div>
            <div className="flex flex-col gap-2">
                <div className="flex flex-col sm:flex-row gap-2">
                    {showAdminSyncButtons && (<Button
                    onClick={handleIncrementalSync}
                    disabled={syncing}
                    variant="outline"
                >
                    <ArrowPathIcon className={`-ml-1 mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? t('salesOrders.syncing', 'finance') : t('salesOrders.syncLatestOrders', 'finance')}
                </Button>
                    )}
                </div>
            </div>
        </div>

        {/* Sync Message */}
        {syncMessage && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-green-700">{syncMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
            <div className="space-y-2 text-left">
                <Label htmlFor="date-from">{t('forms.dateFrom', 'common')}</Label>
                <Input
                    id="start-date"
                    type="date"
                    className="text-left"
                    value={filters.startDate || ''}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
            </div>
            <div className="space-y-2 text-left">
                <Label htmlFor="end-date">{t('forms.dateTo', 'common')}</Label>
                <Input
                    id="end-date"
                    type="date"
                    className="text-left"
                    value={filters.endDate || ''}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
            </div>
            <div className="space-y-2 text-left">
                <Label htmlFor="location">{t('salesOrders.location', 'finance')}</Label>
                <Select value={filters.costCenterId || "all"} onValueChange={(value) => handleFilterChange('costCenterId', value === "all" ? "" : value)}>
                    <SelectTrigger className="w-48">
                        <SelectValue placeholder={t('salesOrders.allLocations', 'finance')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('salesOrders.allLocations', 'finance')}</SelectItem>
                        {costCenters.map((costCenter) => (
                            <SelectItem key={costCenter.id} value={costCenter.id}>
                                {costCenter.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2 text-left">
                <Label>{t('tags.filterByTags', 'common')}</Label>
                <TagFilter
                    entityType="orders"
                    onFilterChange={(selection) => {
                        setTagFilteredIds(selection ? selection.tagIds : null);
                    }}
                />
            </div>
        </div>



        {/* Orders Table */}
        <div className="">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('salesOrders.orderDetails', 'finance')}</TableHead>
                <TableHead>{t('salesOrders.location', 'finance')}</TableHead>
                <TableHead>{t('salesOrders.dateTime', 'finance')}</TableHead>
                <TableHead>{t('salesOrders.amount', 'finance')}</TableHead>
                <TableHead>{t('salesOrders.details', 'finance')}</TableHead>
                <TableHead>{t('salesOrders.source', 'finance')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="flex flex-col items-center">
                      <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">{t('salesOrders.noOrders', 'finance')}</h3>
                      <p className="mt-1 text-sm text-gray-500">{t('salesOrders.noMatch', 'finance')}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                orders
                  .filter((order) => !tagFilteredIds || tagFilteredIds.includes(order.id))
                  .map((order) => (
                  <TableRow
                    key={order.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate({ to: `/sales/orders/${order.id}`})}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {order.external_order_id || `${t('salesOrders.order', 'finance')} ${order.id.slice(0, 8)}`}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {order.table_name && `${t('salesOrders.table', 'finance')}: ${order.table_name}`}
                          {order.guest_count && ` • ${order.guest_count} ${t('salesOrders.guests', 'finance')}`}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {order.cost_center_name || t('salesOrders.unknownLocation', 'finance')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {formatDateTime(order.closed_at || order.opened_at)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {order.is_takeaway ? t('salesOrders.takeaway', 'finance') : t('salesOrders.dineIn', 'finance')}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {formatCurrency(order.total_amount, order.currency_code || 'HUF')}
                        </div>
                        {order.net_amount && (
                          <div className="text-sm text-muted-foreground">
                            {t('salesOrders.net', 'finance')}: {formatCurrency(order.net_amount, order.currency_code || 'HUF')}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>
                          {order.waiter_name && `${t('salesOrders.waiter', 'finance')}: ${order.waiter_name}`}
                        </div>
                        <div>
                          {order.payment_method ? `${t('salesOrders.payment', 'finance')}: ${order.payment_method.name}` :
                           order.payment_type ? `${t('salesOrders.payment', 'finance')}: ${order.payment_type}` : ''}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getDataSourceBadge(order.data_source)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <PaginationFooter
          pagination={pagination}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
        />
      </div>
    </Layout>
  );
};

export default OrdersPage;

export const Route = createFileRoute('/sales/orders/')({
  component: OrdersPage,
})
